import { apiFetch } from './api';

// ============================================================
// Explorateur Mongo (type Compass) par environnement.
// Le navigateur ne parle JAMAIS à Mongo directement : ce service tape le
// backend recube-cicd, qui résout l'ip:port du service Mongo de l'env et
// exécute les requêtes via un driver Mongo.
//
// Contrat backend (à implémenter côté recube-cicd) :
//   GET  /api/projects/{pid}/mongo/{env}/databases
//   GET  /api/projects/{pid}/mongo/{env}/{db}/collections
//   POST /api/projects/{pid}/mongo/{env}/{db}/{coll}/find      { filter?, sort?, projection?, limit?, skip? }
//   POST /api/projects/{pid}/mongo/{env}/{db}/{coll}/insertOne { document }
//   POST /api/projects/{pid}/mongo/{env}/{db}/{coll}/updateOne { filter, update }
//   POST /api/projects/{pid}/mongo/{env}/{db}/{coll}/deleteOne { filter }
// Règle : env "prod" = lecture seule côté backend (les writes doivent renvoyer 403).
// ============================================================

export interface MongoDatabase {
  name: string;
  sizeOnDisk?: number;
  collections?: number;
}

export interface MongoCollectionInfo {
  name: string;
  count?: number;
  size?: number;
}

export interface MongoFindResult {
  documents: Record<string, unknown>[];
  total?: number;
}

/** Résultat de copie pour une collection donnée. */
export interface MongoCopyResult {
  db?: string;
  collection: string;
  status: 'copied' | 'skipped' | 'source-missing' | 'error';
  reason?: string;
  documents?: number;
  indexes?: number;
}

/**
 * Copie de structure/données entre bases Mongo.
 * - portée `collections` : les `collections` de `sourceDb` → `targetDb`
 * - portée `database`    : toutes les collections de `sourceDb` → `targetDb`
 * - portée `environment` : toutes les bases (hors admin/config/local) de l'env → même noms
 * La cible peut être un AUTRE projet (`targetProject`, défaut = projet source) et un autre env
 * (jamais « prod » : bloqué 403 côté backend). Collections déjà présentes = sautées.
 */
export interface MongoCopyRequest {
  sourceEnv: string;
  targetProject?: string;
  targetEnv: string;
  mode: 'collections' | 'database' | 'environment';
  sourceDb?: string;
  targetDb?: string;
  collections?: string[];
  withData: boolean;
}

const base = (pid: string, env: string) => `/api/projects/${pid}/mongo/${env}`;
const enc = encodeURIComponent;

export const mongoService = {
  databases: (pid: string, env: string) =>
    apiFetch<MongoDatabase[]>(`${base(pid, env)}/databases`),

  collections: (pid: string, env: string, db: string) =>
    apiFetch<MongoCollectionInfo[]>(`${base(pid, env)}/${enc(db)}/collections`),

  find: (
    pid: string, env: string, db: string, coll: string,
    body: { filter?: unknown; sort?: unknown; projection?: unknown; limit?: number; skip?: number },
  ) => apiFetch<MongoFindResult>(`${base(pid, env)}/${enc(db)}/${enc(coll)}/find`, { method: 'POST', json: body }),

  insertOne: (pid: string, env: string, db: string, coll: string, document: unknown) =>
    apiFetch<{ ok: boolean; insertedId?: string }>(
      `${base(pid, env)}/${enc(db)}/${enc(coll)}/insertOne`, { method: 'POST', json: { document } }),

  updateOne: (pid: string, env: string, db: string, coll: string, filter: unknown, update: unknown) =>
    apiFetch<{ ok: boolean; matched?: number; modified?: number }>(
      `${base(pid, env)}/${enc(db)}/${enc(coll)}/updateOne`, { method: 'POST', json: { filter, update } }),

  deleteOne: (pid: string, env: string, db: string, coll: string, filter: unknown) =>
    apiFetch<{ ok: boolean; deleted?: number }>(
      `${base(pid, env)}/${enc(db)}/${enc(coll)}/deleteOne`, { method: 'POST', json: { filter } }),

  /** Copie structure (+ données optionnelles) entre bases/projets. `pid` = projet SOURCE. */
  copy: (pid: string, req: MongoCopyRequest) =>
    apiFetch<{ results: MongoCopyResult[] }>(
      `/api/projects/${pid}/mongo-copy`, { method: 'POST', json: req }),
};
